import React from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Loading states shaped like the pages they stand in for, so the page does not
 * jump when the data lands.
 */

/** One question card: number, label, help line, four options. */
const QuestionCardSkeleton: React.FC = () => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
    <div className="flex items-baseline gap-2.5">
      <Skeleton className="h-3 w-5" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <Skeleton className="ml-0 mt-2 h-3 w-11/12 sm:ml-8" />
    <div className="ml-0 mt-3.5 space-y-1.5 sm:ml-8">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton className="h-9 w-full rounded-lg" key={i} />
      ))}
    </div>
  </div>
);

/** The fill screen: questions on the left, result rail on the right. */
export const DiagnosticFillSkeleton: React.FC = () => (
  <MainPageWrapper title="Diagnostic">
    <div className="space-y-6">
      {Array.from({ length: 2 }).map((_, s) => (
        <section
          className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
          key={s}
        >
          <div className="mb-1 flex items-center gap-2">
            <Skeleton className="h-5 w-56" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <Skeleton className="mb-3 h-3 w-4/5" />
          <div className="space-y-3">
            {Array.from({ length: s === 0 ? 2 : 3 }).map((_, q) => (
              <QuestionCardSkeleton key={q} />
            ))}
          </div>
        </section>
      ))}
    </div>
  </MainPageWrapper>
);

/** The companies directory table. */
export const DiagnosticCompaniesSkeleton: React.FC<{ rows?: number }> = ({
  rows = 6,
}) => (
  <MainPageWrapper title="Diagnostics">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <Skeleton className="h-4 w-96" />
      <div className="flex items-center gap-3">
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-9 w-36" />
      </div>
    </div>

    <div className="mt-6 rounded-md border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          className="grid grid-cols-12 items-center gap-4 border-b border-gray-200 p-4 last:border-b-0 dark:border-slate-700"
          key={i}
        >
          <div className="col-span-4">
            <Skeleton className="h-4 w-2/3" />
          </div>
          <div className="col-span-3">
            <Skeleton className="h-3 w-3/4" />
          </div>
          <div className="col-span-2">
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <div className="col-span-2">
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <div className="col-span-1 flex justify-end">
            <Skeleton className="h-4 w-6" />
          </div>
        </div>
      ))}
    </div>
  </MainPageWrapper>
);

/** One company's history: header, then the attempts table. */
export const DiagnosticCompanyDetailSkeleton: React.FC<{ rows?: number }> = ({
  rows = 3,
}) => (
  <MainPageWrapper title="Company">
    <Skeleton className="h-8 w-40" />

    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <Skeleton className="h-5 w-56" />
      <Skeleton className="mt-2 h-3 w-40" />
    </div>

    <div className="mt-8 rounded-md border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          className="grid grid-cols-12 items-center gap-4 border-b border-gray-200 p-4 last:border-b-0 dark:border-slate-700"
          key={i}
        >
          <div className="col-span-4 space-y-1">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <div className="col-span-2">
            <Skeleton className="h-3 w-20" />
          </div>
          <div className="col-span-2">
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <div className="col-span-2">
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <div className="col-span-2 flex justify-end">
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      ))}
    </div>
  </MainPageWrapper>
);
